


















































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import {
  AccountStateEnum,
  AccountTypeEnum,
  AgentTypeEnum,
  OCTBAccountPermissionEnum,
} from '@/api/types';
import {
  get_account,
  getAccount,
  put_account,
  putAccount,
} from '@/api/account';
import { list_agent_summaries, listAgentSummaries } from '@/api/agent';
import {
  formatApiError,
  stringFromIDate,
  monthStringFromIDate,
} from '@/utils/index';
import { AppRoleType } from '@/utils/types';

type WithUpdatePassword = {
  updatePassword: boolean;
};

type ItemType = {
  text: string;
  value: string;
};

@Component({
  name: 'AccountDetail',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  @Prop({ required: true }) private id?: string;
  private readonly defaultEditingAccount: put_account.Account &
    WithUpdatePassword = {
    id: '',
    state: AccountStateEnum.ACTIVE,
    updatePassword: false,
    octbAccountPermissions: [],
  };
  private account = {} as get_account.Account;
  private loading = true;
  private editing = false;
  private editingAccount = Object.assign({}, this.defaultEditingAccount);
  private agentItems: ItemType[] = [];
  private octbAccountPermissionItems: ItemType[] = [];
  private editingAccountUpdatePassword = false;
  private editingAccountShowPassword = false;
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get canEdit() {
    return (
      !this.editing &&
      this.account.id &&
      UserModule.roles.some(
        r =>
          (r as AppRoleType) === AppRoleType.ADMIN ||
          (r as AppRoleType) === AppRoleType.SUPPLIER
      )
    );
  }

  get canUpdateAccountType() {
    return false; // TODO: support update account type when needed
  }

  get canUpdateAgentId() {
    return (
      this.editingAccount.type &&
      (this.editingAccount.type as AccountTypeEnum) === AccountTypeEnum.AGT
    );
  }

  get accountTypeItems(): ItemType[] {
    return Object.values(AccountTypeEnum)
      .filter(
        t => (t as AccountTypeEnum) !== AccountTypeEnum.UNKNOWN_ACCOUNT_TYPE
      )
      .map(t => ({
        text: this.$t(`AccountTypeEnum.${t}`) as string,
        value: t,
      }));
  }

  get accountStateFalseValue() {
    return AccountStateEnum.INACTIVE;
  }

  get accountStateTrueValue() {
    return AccountStateEnum.ACTIVE;
  }

  get showOctbAccountPermissions() {
    return !this.editing
      ? (this.account.type as AccountTypeEnum) !== AccountTypeEnum.AGT
      : (this.editingAccount.type as AccountTypeEnum) !== AccountTypeEnum.AGT;
  }

  get canUpdate() {
    return true;
  }

  mounted() {
    this.fetchData();
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForUpdate() {
    this.dialogTextTitle = `${this.$t('Update {msg}', {
      msg: this.$t('Account'),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.updateAccount();
    };
    this.dialog = true;
  }

  private showDialogForResetEditing() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.startEditingAccount();
    };
    this.dialog = true;
  }

  private showDialogForAbort() {
    this.dialogTextTitle = `${this.$t('Cancel {msg}', {
      msg: this.$t('Update {msg}', {
        msg: this.$t('Account'),
      }),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.abortEditingAccount();
    };
    this.dialog = true;
  }

  private async fetchData(noLoading = false) {
    if (!noLoading) {
      this.loading = true;
      AppModule.StartProgress();
    }

    try {
      const res = await getAccount({
        id: this.id,
      } as get_account.Request);
      // if (
      //   !this.canUpdateAccountType &&
      //   (res.account.type as AccountTypeEnum) !== AccountTypeEnum.AGT
      // ) {
      //   throw new Error(this.$t('Permission denied') as string);
      // }
      this.account = res.account;

      if (!noLoading) {
        AppModule.StopProgress();
        this.loading = false;
      }
    } catch (err) {
      if (!noLoading) {
        AppModule.StopProgress();
        this.loading = false;
      }
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async loadAgentItems() {
    try {
      const listAgentsRes = await listAgentSummaries({
        agentTypes: Object.values(AgentTypeEnum).filter(
          t => (t as AgentTypeEnum) !== AgentTypeEnum.UNKNOWN_AGENT_TYPE
        ),
      } as list_agent_summaries.Request);
      const items: ItemType[] = [];
      listAgentsRes.agentSummaries.forEach(a => {
        items.push({
          text: a.name,
          value: a.id,
        });
      });
      this.agentItems = items;
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Agent'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private loadOctbAccountPermissionItems() {
    const items: ItemType[] = [];
    Object.values(OCTBAccountPermissionEnum).forEach(e => {
      if (
        (e as OCTBAccountPermissionEnum) !==
        OCTBAccountPermissionEnum.UNKNOWN_ROLE
      ) {
        items.push({
          text: this.$t(`OCTBAccountPermissionEnum.${e}`) as string,
          value: e,
        });
      }
    });
    this.octbAccountPermissionItems = items;
  }

  private async startEditingAccount() {
    if (this.agentItems.length === 0) {
      await this.loadAgentItems();
    }
    if (this.octbAccountPermissionItems.length === 0) {
      await this.loadOctbAccountPermissionItems();
    }
    this.editingAccount = {
      type: this.account.type,
      id: this.account.id,
      agentId: this.account.agentId || '',
      state: this.account.state,
      octbAccountPermissions: this.account.octbAccountPermissions,
      password: '',
      version: this.account.version,
      updatePassword: false,
    };
    this.editing = true;
  }

  private abortEditingAccount() {
    this.editing = false;
    this.editingAccount = Object.assign({}, this.defaultEditingAccount);
    this.editingAccountUpdatePassword = false;
    this.editingAccountShowPassword = false;
  }

  private async updateAccount() {
    this.loading = true;
    AppModule.StartProgress();

    try {
      const account: put_account.Account = {
        id: this.editingAccount.id,
        state: this.editingAccount.state,
        octbAccountPermissions: this.editingAccount.octbAccountPermissions,
        version: this.editingAccount.version,
      };
      if (this.editingAccountUpdatePassword) {
        account.password = this.editingAccount.password;
      }
      if (this.editingAccount.type) {
        account.type = this.editingAccount.type;
        if (
          (this.editingAccount.type as AccountTypeEnum) === AccountTypeEnum.AGT
        ) {
          account.agentId = this.editingAccount.agentId;
        }
      }

      await putAccount({
        account: account,
      } as put_account.Request);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Update {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }

    this.abortEditingAccount();
    await this.fetchData(true);
    AppModule.StopProgress();
    this.loading = false;
  }

  private getAccountStateText(state: AccountStateEnum) {
    return (state as AccountStateEnum) === AccountStateEnum.ACTIVE
      ? this.$t('Active')
      : this.$t('Inactive');
  }

  private getOCTBAccountPermissionText(permission: OCTBAccountPermissionEnum) {
    return this.$t(`OCTBAccountPermissionEnum.${permission}`) as string;
  }
}
